import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ArrowUp, CalendarHeart } from "lucide-react";
import { business, whatsappMessages, waLink } from "@/config/site";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.599 5.317l-.999 3.648 3.9-1.022zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

export const FloatingButtons = ({ onInquiry }) => {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Right stack */}
      <div className="fixed right-4 sm:right-6 bottom-6 z-[9990] flex flex-col items-end gap-3">
        <a
          data-testid="whatsapp-float-btn"
          href={waLink(whatsappMessages.general)}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/20 transition-transform duration-300 hover:scale-110"
          aria-label="Chat on WhatsApp"
        >
          <WhatsAppIcon className="h-7 w-7" />
        </a>
        <a
          data-testid="call-float-btn"
          href={`tel:${business.phoneRaw}`}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-maroon text-champagne shadow-lg shadow-black/20 transition-transform duration-300 hover:scale-110"
          aria-label="Call us"
        >
          <Phone className="h-6 w-6" strokeWidth={1.5} />
        </a>
        <AnimatePresence>
          {showTop && (
            <motion.button
              data-testid="back-to-top-btn"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-charcoal text-champagne shadow-lg transition-transform duration-300 hover:scale-110"
              aria-label="Back to top"
            >
              <ArrowUp className="h-5 w-5" strokeWidth={1.5} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Sticky inquiry button (left) */}
      <button
        data-testid="sticky-inquiry-btn"
        onClick={onInquiry}
        className="fixed left-0 top-1/2 z-[9990] hidden -translate-y-1/2 -rotate-90 origin-left items-center gap-2 rounded-b-xl bg-champagne px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-charcoal shadow-lg transition-colors duration-300 hover:bg-maroon hover:text-white md:flex"
      >
        <CalendarHeart className="h-4 w-4 rotate-90" strokeWidth={1.5} />
        Enquire Now
      </button>
    </>
  );
};
